import {
  createActionCableManager,
  type ActionCableManagerAPI,
} from 'components/AI/ActionCableManager'
import { useWebSocketContext } from 'contexts/WebSocketContext'
import type { WebSocketData } from 'hooks/websocket/types'
import { buildTranslate } from 'locales'
import { useCallback, useEffect, useRef } from 'react'
import { useDispatch, useSelector, useStore } from 'react-redux'
import { processMessage } from 'services/websocket/messageHandlers'
import {
  selectConnectionStatus,
  selectWebSocketError,
  setChatId,
  setConnectionError,
  setConnectionStatus,
  setManagerId,
} from 'slices/aiConversations'
import type { Message } from 'slices/aiConversations/types'
import * as uuid from 'uuid'

const t = buildTranslate('components.ai.websocket_connection')

export interface MessageProcessorCallbacks {
  setCurrentStreamingMessage: React.Dispatch<
    React.SetStateAction<Message | null>
  >
  setIsLoading: React.Dispatch<React.SetStateAction<boolean>>
  setAwaitingResponse: React.Dispatch<React.SetStateAction<boolean>>
  addMessageWithOrdering: (message: Message) => void
  removeToolCallMessages: () => void
  sequenceCounter: React.MutableRefObject<number>
}

export interface UseAIChatConnectionReturn {
  isConnected: boolean
  connectionError: string | null
  wsManager: React.MutableRefObject<ActionCableManagerAPI | null>
  startStreamingTimeout: () => void
  resetChatId: () => void
  setMessageDroppedCallback: (callback: (() => void) | null) => void
}

interface AppState {
  auth: {
    token: string | null
    current_merchant_id: number | null
    current_merchant_user_id?: number | null
  }
}

/**
 * Hook for managing WebSocket connection lifecycle.
 *
 * Handles:
 * - Connection establishment and teardown
 * - Authentication and reconnection logic
 * - Manager registration with WebSocket context
 * - Connection status tracking
 */
export const useAIChatConnection = (
  callbacks: MessageProcessorCallbacks
): UseAIChatConnectionReturn => {
  const dispatch = useDispatch()
  const store = useStore()
  const webSocketContext = useWebSocketContext()

  const connectionStatus = useSelector(selectConnectionStatus)
  const connectionError = useSelector(selectWebSocketError)
  const isConnected = connectionStatus === 'connected'

  const token = useSelector((state: AppState) => state.auth.token)
  const merchantId = useSelector(
    (state: AppState) => state.auth.current_merchant_id
  )
  const userId = useSelector(
    (state: AppState) => state.auth.current_merchant_user_id
  )

  const wsManager = useRef<ActionCableManagerAPI | null>(null)
  const managerId = useRef<string | null>(null)

  // Track previous values to detect changes - initialize with null to force initial connection
  const prevMerchantIdRef = useRef<number | null>(null)
  const prevUserIdRef = useRef<number | null | undefined>(null)
  const prevTokenRef = useRef<string | null>(null)

  const getChatId = useCallback(() => {
    const state = store.getState()
    return (
      (
        state as {
          aiConversations?: { webSocket?: { chatId?: string | null } }
        }
      ).aiConversations?.webSocket?.chatId || null
    )
  }, [store])

  // Work with ActionCableManager's existing timeout system
  const startStreamingTimeout = useCallback(() => {
    // This is called by ActionCableManager after successful message send
    // We don't need to add additional timeout logic - the server will send
    // message_complete when done, or error messages if something goes wrong
  }, [])

  const resetChatId = useCallback(() => {
    dispatch(setChatId(null))
  }, [dispatch])

  const messageDroppedCallbackRef = useRef<(() => void) | null>(null)

  const setMessageDroppedCallback = useCallback(
    (callback: (() => void) | null) => {
      const manager = wsManager.current
      // Remove previous callback if present
      if (manager && messageDroppedCallbackRef.current) {
        manager.offMessageDropped(messageDroppedCallbackRef.current)
      }

      if (manager && callback) {
        manager.onMessageDropped(callback)
      }

      messageDroppedCallbackRef.current = callback
    },
    []
  )

  const connectWebSocket = useCallback(async () => {
    if (!token || !merchantId) {
      dispatch(setConnectionError(t('no_auth')))
      return
    }

    try {
      if (wsManager.current) {
        // Unregister callback from old manager
        if (messageDroppedCallbackRef.current) {
          wsManager.current.offMessageDropped(messageDroppedCallbackRef.current)
        }
        wsManager.current.disconnect()
        wsManager.current = null
      }

      if (managerId.current) {
        webSocketContext.unregisterManager(managerId.current)
        managerId.current = null
      }

      const currentChatId = getChatId()
      managerId.current = uuid.v4()
      dispatch(setConnectionStatus('connecting'))
      dispatch(setManagerId(managerId.current))

      wsManager.current = createActionCableManager({
        token,
        merchantId,
        initialChatId: currentChatId,
      })

      // Re-attach stored callback if present
      if (messageDroppedCallbackRef.current && wsManager.current) {
        wsManager.current.onMessageDropped(messageDroppedCallbackRef.current)
      }

      // Register manager in context for thunk access
      if (managerId.current && wsManager.current) {
        webSocketContext.registerManager(managerId.current, wsManager.current)
      }

      // Set up streaming timeout callback for ActionCableManager
      wsManager.current.startStreamingTimeout = startStreamingTimeout

      // Set up message processing using the message handler system
      const messageProcessor = (data: unknown) => {
        if (data && typeof data === 'object' && 'type' in data) {
          const typedData = data as { type: string; content?: string }
          if (typedData.type === 'connection_established') {
            dispatch(setConnectionStatus('connected'))
            dispatch(setConnectionError(null))
            return
          }

          if (typedData.type === 'auth_error') {
            const errorContent = typedData.content || t('auth_error')
            dispatch(setConnectionError(errorContent))
            dispatch(setConnectionStatus('disconnected'))
            return
          }
        }

        // Use the message handler system for all other message types
        const context = {
          getState: () => store.getState(),
          dispatch,
          setCurrentStreamingMessage: callbacks.setCurrentStreamingMessage,
          setIsLoading: callbacks.setIsLoading,
          setAwaitingResponse: callbacks.setAwaitingResponse,
          addMessageWithOrdering: callbacks.addMessageWithOrdering,
          removeToolCallMessages: callbacks.removeToolCallMessages,
          sequenceCounter: callbacks.sequenceCounter,
          setConnectionError: (error: string | boolean) => {
            dispatch(
              setConnectionError(typeof error === 'string' ? error : null)
            )
          },
          setIsConnected: (connected: boolean) => {
            dispatch(
              setConnectionStatus(connected ? 'connected' : 'disconnected')
            )
          },
          clearStreamingTimeout: () => {
            // No-op for now - streaming timeout handled in the main manager
          },
          startStreamingTimeout: () => {
            // No-op for now - streaming timeout handled in the main manager
          },
        }

        processMessage(data as WebSocketData, context)
      }

      wsManager.current.onMessage(messageProcessor)
      await wsManager.current.connect()
    } catch (error) {
      const errorMessage =
        error instanceof Error ? error.message : String(error)
      dispatch(setConnectionError(errorMessage))
      dispatch(setConnectionStatus('disconnected'))
    }
  }, [
    token,
    merchantId,
    dispatch,
    getChatId,
    startStreamingTimeout,
    callbacks,
    webSocketContext,
    store,
    t,
  ])

  // Effect to handle authentication and reconnection
  useEffect(() => {
    const hasAuthChanged =
      prevTokenRef.current !== token ||
      prevMerchantIdRef.current !== merchantId ||
      prevUserIdRef.current !== userId

    if (hasAuthChanged) {
      prevTokenRef.current = token
      prevMerchantIdRef.current = merchantId
      prevUserIdRef.current = userId

      if (token && merchantId) {
        connectWebSocket()
      } else {
        // No auth - disconnect
        if (wsManager.current) {
          wsManager.current.disconnect()
          wsManager.current = null
        }
        if (managerId.current) {
          webSocketContext.unregisterManager(managerId.current)
          managerId.current = null
        }
        dispatch(setConnectionStatus('disconnected'))
      }
    }
  }, [token, merchantId, userId, connectWebSocket, dispatch, webSocketContext])

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (wsManager.current) {
        wsManager.current.disconnect()
      }
      if (managerId.current) {
        webSocketContext.unregisterManager(managerId.current)
      }
    }
  }, [webSocketContext])

  return {
    isConnected,
    connectionError,
    wsManager,
    startStreamingTimeout,
    resetChatId,
    setMessageDroppedCallback,
  }
}
